import { useCallback } from 'react';

const OPTION_LABELS = ['MT', 'TA', 'STK', 'PW', 'VR', 'CP'];

// Sort items by `sortOrder` (if explicitly set), falling back to their natural
// position in the input array. This lets user-added items slot in between
// conveyor-derived items via fractional sortOrder values (e.g. 1.5).
const applySortOrder = (items) =>
  items
    .map((item, idx) => ({
      item,
      idx,
      eff: item.sortOrder != null ? Number(item.sortOrder) : idx,
    }))
    .sort((a, b) => a.eff - b.eff || a.idx - b.idx)
    .map((x) => x.item);

// Split a section's items into the "Primary" and "Options" sub-tables.
// `userSection` overrides the default specialLabel-based split so a user-added
// item dropped into e.g. "InFeed Options" lands in the options table even
// though it has no specialLabel.
const separatePrimaryAndOptions = (items, sectionPrefix) => {
  const primary = items.filter((item) => {
    if (item.userSection === `${sectionPrefix}-options`) return false;
    if (item.userSection === `${sectionPrefix}-primary`) return true;
    return !OPTION_LABELS.includes(item.specialLabel);
  });
  const options = items.filter((item) => {
    if (item.userSection === `${sectionPrefix}-options`) return true;
    if (item.userSection === `${sectionPrefix}-primary`) return false;
    return OPTION_LABELS.includes(item.specialLabel);
  });
  return { primary: applySortOrder(primary), options: applySortOrder(options) };
};

// Calculate global indices for all items
export const useGlobalIndices = (infeedItems, outfeedItems, newItems, additionalItems) => {
  return useCallback(() => {
    const { primary: infeedPrimary, options: infeedOptions } = separatePrimaryAndOptions(infeedItems, 'infeed');
    const { primary: outfeedPrimary, options: outfeedOptions } = separatePrimaryAndOptions(outfeedItems, 'outfeed');

    // Combine in order: infeed primary, infeed options, outfeed primary, outfeed options, new, additional
    const allItems = [
      ...infeedPrimary.map(item => ({ ...item, section: 'infeed' })),
      ...infeedOptions.map(item => ({ ...item, section: 'infeed' })),
      ...outfeedPrimary.map(item => ({ ...item, section: 'outfeed' })),
      ...outfeedOptions.map(item => ({ ...item, section: 'outfeed' })),
      ...applySortOrder(newItems).map(item => ({ ...item, section: 'new' })),
      ...additionalItems.map(item => ({ ...item, section: 'additional' }))
    ];
    return allItems.map((item, index) => ({
      ...item,
      globalIndex: index + 1
    }));
  }, [infeedItems, outfeedItems, newItems, additionalItems]);
};

// Split items back into sections with global indices
export const useSectionItemsWithIndices = (getGlobalIndices) => {
  return useCallback(() => {
    const allItemsWithIndices = getGlobalIndices();
    return {
      infeed: allItemsWithIndices.filter(item => item.section === 'infeed'),
      outfeed: allItemsWithIndices.filter(item => item.section === 'outfeed'),
      new: allItemsWithIndices.filter(item => item.section === 'new'),
      additional: allItemsWithIndices.filter(item => item.section === 'additional')
    };
  }, [getGlobalIndices]);
};
